import React from "react";
import styles from "./index.module.scss";
import { applyLocaleToUrl } from "@/utils/formatters";
import { useRouter } from "next/router";

const InlineMediaBlock = ({ inlineMediaBlockData = {} }) => {
  const { locale } = useRouter();
  const {
    mainTitle,
    body = [],
    orientation,
    firstHeading = false,
    isAboutUs = false,
  } = inlineMediaBlockData;

  const renderTextNode = (node, key) => {
    if (node.nodeType === "text") {
      let text = node.value;

      node.marks?.forEach(mark => {
        if (mark.type === "bold") {
          text = <strong key={key}>{text}</strong>;
        }
        if (mark.type === "italic") {
          text = <em key={key}>{text}</em>;
        }
      });

      return text;
    }

    if (node.nodeType === "hyperlink") {
      let hrefs = node.data.uri || "";
      const isExternal = hrefs.includes("http");

      return (
        <a
          key={key}
          href={applyLocaleToUrl(hrefs, locale)}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-[#337ab7]"
        >
          {node.content.map((child, i) => renderTextNode(child, i))}
        </a>
      );
    }

    return null;
  };

  const renderRichText = nodes => {
    if (!nodes) return null;

    return nodes.map((node, index) => {
      switch (node.nodeType) {
        case "heading-1":
        case "heading-2":
        case "heading-3":
          return (
            <h3 key={index} className="koh-simple-content-subtitle">
              {node.content.map((n, i) => renderTextNode(n, i))}
            </h3>
          );

        case "paragraph":
          return (
            <p key={index} className="koh-simple-content-paragraph">
              {node.content.map((n, i) => renderTextNode(n, i))}
            </p>
          );

        case "unordered-list":
          return (
            <ul key={index} className="koh-simple-content-list">
              {node.content.map((li, liIndex) => (
                <li key={liIndex}>
                  {li.content.map(c =>
                    c.content.map((n, i) => renderTextNode(n, i))
                  )}
                </li>
              ))}
            </ul>
          );

        default:
          return null;
      }
    });
  };

  const pairs = [];

  for (let i = 0; i < body.length; i++) {
    const item = body[i];
    const type = item?.sys?.contentType?.sys?.id;

    if (type === "imagesItem") {
      const nextItem = body[i + 1];
      if (nextItem?.sys?.contentType?.sys?.id === "contentItem") {
        pairs.push({
          image: item.fields,
          content: nextItem.fields,
        });
        i++;
      }
    } else if (type === "contentItem") {
      pairs.push({
        image: null,
        content: item.fields,
      });
    }
  }

  let mainTitleClass = firstHeading
    ? "koh-simple-content-first-title"
    : "koh-simple-content-title";

  return (
    <div className={styles.wrapper}>
      {mainTitle && (
        <section className="c-koh-simple-content v-koh-snippet">
          <div className="koh-simple-content-container">
            <h2 className={mainTitleClass}>{mainTitle}</h2>
          </div>
        </section>
      )}

      {pairs.map((pair, index) => {
        const { image, content } = pair;
        const imageUrl =
          image?.imageUrl || image?.assets?.[0]?.fields?.file?.url || null;
        const isImageLeft =
          pairs.length === 1 ? orientation !== "right" : index % 2 === 0;
        const isAboutUsTitleClass = isAboutUs
          ? "koh-simple-content-title-aboutus"
          : "koh-simple-content-title";
        const imageWidth = isAboutUs ? 240 : 400;
        const imageHeight = isAboutUs ? 175 : 290;
        const divClassName = isAboutUs ? "div-about-us" : "div-media-block";

        return (
          <>
            <section
              key={index}
              className={`c-koh-simple-content v-koh-snippet ${isAboutUs ? "line w-full" : ""}
              m-koh-wrap-content
              ${isImageLeft ? "m-koh-image-left" : "m-koh-image-right"}
              ${image?.imageUrl ? "" : "not-having-image"}
            `}
            >
              <div className={divClassName}>
                {imageUrl && (
                  <div className="koh-simple-content-image">
                    <img
                      src={imageUrl}
                      alt={image.alt || ""}
                      style={{
                        maxWidth: `${image.imageWidth || imageWidth}px`,
                        maxHeight: `${image.imageHeight || imageHeight}px`,
                      }}
                    />
                  </div>
                )}

                <div className="koh-simple-content-container">
                  {content.heading && (
                    <h2 className={isAboutUsTitleClass}>{content.heading}</h2>
                  )}

                  <div className="koh-simple-content-body">
                    {renderRichText(content.description?.content)}
                  </div>
                </div>
              </div>
            </section>
          </>
        );
      })}
    </div>
  );
};

export default InlineMediaBlock;
